import type {
  ActivityId,
  ExecutionResourceId,
  ProjectId,
  ServiceId,
  TeamId,
  TeamMeasurementId,
  UserId,
} from '../shared/ids';

/**
 * Medir por equipe (Fase 30 — D1 a D7, RN-405 a RN-407).
 *
 * A obra escolhe como a Conferência mede a quantidade:
 *
 * - **por pessoa** (o padrão, o de sempre): cada pessoa lança o que produziu na alocação dela;
 * - **por equipe**: a quantidade é lançada **uma vez** por equipe × frente × serviço no dia, e a
 *   alocação de quem está na equipe guarda só as horas. Horas, ausência e hora extra continuam
 *   sendo de cada pessoa — a folha, o custo e a jornada são de cada um (D1).
 *
 * Quem não tem equipe e o terceirizado medem como hoje, mesmo na obra por equipe (D4): não há
 * de quem mais ser a quantidade. A troca de modo vale para os dias ainda não conferidos (D3).
 */
export type MeasurementMode = 'POR_PESSOA' | 'POR_EQUIPE';

export const MEASUREMENT_MODES: readonly MeasurementMode[] = ['POR_PESSOA', 'POR_EQUIPE'];

export const MEASUREMENT_MODE_LABEL: Readonly<Record<MeasurementMode, string>> = {
  POR_PESSOA: 'Por pessoa',
  POR_EQUIPE: 'Por equipe',
};

export const DEFAULT_MEASUREMENT_MODE: MeasurementMode = 'POR_PESSOA';

/** A medição de uma equipe numa frente × serviço no dia (RN-406). */
export interface TeamMeasurement {
  readonly id: TeamMeasurementId;
  readonly projectId: ProjectId;
  readonly teamId: TeamId;
  readonly activityId: ActivityId;
  /** A unidade vem do serviço (RN-232): frente sem serviço não se mede. */
  readonly serviceId: ServiceId;
  /** `AAAA-MM-DD`. */
  readonly date: string;
  readonly quantity: number;
  /**
   * Quanto da quantidade não está conforme (Fase 37, RN-475): a maior das NCs vivas desta
   * medição. Sai da produção, como na alocação da pessoa (`acceptedQuantity`).
   */
  readonly nonConformingQuantity?: number;
  readonly measuredByUserId?: UserId;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Uma por equipe × frente × serviço × dia — a chave que o banco também garante. */
export function teamMeasurementNaturalKey(input: {
  readonly teamId: string;
  readonly activityId: string;
  readonly serviceId: string;
  readonly date: string;
}): string {
  return `${input.teamId}|${input.activityId}|${input.serviceId}|${input.date}`;
}

/** O que a conferência precisa saber de uma alocação do dia para agrupar por equipe. */
export interface AllocationOfDay {
  readonly resourceId: ExecutionResourceId;
  readonly activityId: ActivityId;
  readonly serviceId?: ServiceId;
  readonly quarterHours: number;
  readonly plannedQuantity?: number;
}

/** Uma frente × serviço de uma equipe no dia, com quem trabalhou nela (RN-406). */
export interface TeamFrontOfDay {
  readonly teamId: TeamId;
  readonly activityId: ActivityId;
  readonly serviceId: ServiceId;
  readonly resourceIds: readonly ExecutionResourceId[];
  readonly quarterHours: number;
  /** A meta da equipe: a soma das metas de quem foi alocado (até a etapa 3). */
  readonly plannedQuantity?: number;
}

/**
 * As frentes × serviços que cada equipe tem para medir no dia.
 *
 * Entra a alocação de quem está numa equipe naquele dia (`teamOf`, pela filiação aberta na data —
 * RN-053) e tem serviço: sem serviço não há unidade, e não há o que medir. Duas equipes na mesma
 * frente × serviço medem cada uma a sua. A ordem é a das alocações recebidas.
 */
export function teamFrontsOfDay(
  allocations: readonly AllocationOfDay[],
  teamOf: (resourceId: ExecutionResourceId) => TeamId | undefined,
): readonly TeamFrontOfDay[] {
  const porChave = new Map<
    string,
    {
      teamId: TeamId;
      activityId: ActivityId;
      serviceId: ServiceId;
      resourceIds: ExecutionResourceId[];
      quarterHours: number;
      plannedQuantity?: number;
    }
  >();
  for (const allocation of allocations) {
    if (allocation.serviceId === undefined) continue;
    const teamId = teamOf(allocation.resourceId);
    if (teamId === undefined) continue;
    const chave = `${teamId}|${allocation.activityId}|${allocation.serviceId}`;
    let frente = porChave.get(chave);
    if (frente === undefined) {
      frente = {
        teamId,
        activityId: allocation.activityId,
        serviceId: allocation.serviceId,
        resourceIds: [],
        quarterHours: 0,
      };
      porChave.set(chave, frente);
    }
    if (!frente.resourceIds.includes(allocation.resourceId)) {
      frente.resourceIds.push(allocation.resourceId);
    }
    frente.quarterHours += allocation.quarterHours;
    if (allocation.plannedQuantity !== undefined) {
      frente.plannedQuantity = (frente.plannedQuantity ?? 0) + allocation.plannedQuantity;
    }
  }
  return [...porChave.values()];
}

/**
 * A quantidade desta alocação é da equipe? Só na obra por equipe, e só de quem está numa equipe
 * (RN-406, RN-407). Quem responde "sim" não grava quantidade na alocação.
 */
export function quantityBelongsToTeam(mode: MeasurementMode, teamId: TeamId | undefined): boolean {
  return mode === 'POR_EQUIPE' && teamId !== undefined;
}
